<script setup lang="ts">
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppLayout from './components/AppLayout.vue';
import TwoFactorPanel from './components/settings/TwoFactorPanel.vue';
import ConfirmHost from './components/ConfirmHost.vue';
import UpgradeHost from './components/plan/UpgradeHost.vue';
import { useAuthStore } from './stores/auth.store';
import { startPresence } from './stores/presence';
import { bootError } from './utils/boot-error';

const retry = (): void => window.location.reload();

const { t } = useI18n();

const auth = useAuthStore();
// Live player connections for every status in the CMS: once signed in, and again for another organization.
watch(
  () => (auth.isAuthenticated ? auth.activeOrganization?.id : null),
  (org) => org && startPresence(),
  { immediate: true },
);
</script>

<template>
  <!-- The first page couldn't open (server waking up, page code failed to load): never a blank page. -->
  <div v-if="bootError" class="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
    <div class="card max-w-md space-y-3 p-6 text-center">
      <h1 class="text-lg font-semibold">{{ t('misc.bootTitle') }}</h1>
      <p class="text-sm text-slate-600 dark:text-slate-300">{{ t('misc.bootBody') }}</p>
      <button type="button" class="btn-primary" @click="retry">{{ t('misc.bootRetry') }}</button>
      <p class="break-words text-xs text-slate-400">{{ bootError }}</p>
    </div>
  </div>
  <!-- Signed in: the app shell (sidebar, organization switcher, account). Signed out: bare public pages. -->
  <AppLayout v-else-if="auth.isAuthenticated">
    <p v-if="!auth.activeOrganization" class="alert-warn">
      {{ t('misc.noOrg') }}
    </p>
    <!-- Suspended by Displaro: the CMS is locked (the platform admin is its own app at /admin). -->
    <div v-else-if="auth.activeOrganization.suspended && !$route.meta.public" class="card mx-auto mt-10 max-w-lg space-y-2 p-6">
      <h1 class="text-lg font-semibold">{{ t('admin.suspendedOrgTitle', { org: auth.activeOrganization.name }) }}</h1>
      <p class="text-sm text-slate-600 dark:text-slate-300">{{ t('admin.suspendedOrgBody') }}</p>
    </div>
    <!-- The organization requires two-factor sign-in and this person hasn't turned it on: set it up first. -->
    <div v-else-if="auth.activeOrganization.require2fa && !auth.user?.twoFactorEnabled && !$route.meta.public" class="mx-auto mt-6 max-w-2xl space-y-4">
      <div class="alert-warn">
        <p class="font-medium">{{ t('tfa.requiredTitle', { org: auth.activeOrganization.name }) }}</p>
        <p>{{ t('tfa.requiredBody') }}</p>
      </div>
      <TwoFactorPanel required />
    </div>
    <!-- Re-mount tenant pages when the active org changes; never re-mount public pages (invite accept). -->
    <RouterView v-else :key="$route.meta.public ? 'public' : auth.activeOrganization.id" />
  </AppLayout>
  <div v-else class="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
    <RouterView />
  </div>
  <ConfirmHost />
  <UpgradeHost />
</template>
